import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  DeviceContainer,
  DeviceTitle,
  SectionTitle,
  DeviceTable,
  TableHeader,
  TableCell,
  StatusBadge,
  ActionButton,
  HeaderContainer,
  LoadingContainer,
  ErrorContainer
} from './DeviceManagement.styles';



interface Device {
  id: string;
  serialNumber: string;
  sku: string;
  customerName: string;
  condition: string;
  status: string;
  issue: string;
  receivedDate: string;
  receivedBy: string;
  updatedDate?: string;
  updatedBy?: string;
}

const DeviceManagement: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [formData, setFormData] = useState<Partial<Device>>({
    serialNumber: '',
    sku: '',
    customerName: '',
    condition: 'good',
    status: 'received',
    issue: ''
  });

  const { getAuthToken } = useAuth();

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://17igbr2mm5.execute-api.eu-west-1.amazonaws.com/dev';
  
  const getAuthHeaders = async () => {
    const token = await getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/devices`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch devices: ${response.statusText}`);
      }

      const data = await response.json();
      setDevices(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch devices');
      console.error('Error fetching devices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (device: Device) => {
    setEditingDevice(device);
    setFormData(device);
    setShowForm(true);
  };

  const handleDeleteDevice = async (device: Device) => {
    if (!confirm(`Are you sure you want to delete device ${device.serialNumber}?`)) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/devices/${device.id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to delete device: ${response.statusText}`);
      }

      await fetchDevices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete device');
      console.error('Error deleting device:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      if (editingDevice) {
        // Update existing device
        const url = `${API_BASE_URL}/devices/${editingDevice.id}`;
        const headers = await getAuthHeaders();
        const response = await fetch(url, {
          method: 'PUT',
          headers,
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error(`Failed to update device: ${response.statusText}`);
        }
      } else {
        // Create new device
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/devices`, {
          method: 'POST',
          headers,
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error(`Failed to create device: ${response.statusText}`);
        }
      }

      resetForm();
      await fetchDevices();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${editingDevice ? 'update' : 'create'} device`);
      console.error('Error submitting device:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      serialNumber: '',
      sku: '',
      customerName: '',
      condition: 'good',
      status: 'received',
      issue: ''
    });
    setShowForm(false);
    setEditingDevice(null);
    setError(null);
  };


  if (loading) {
    return (
      <LoadingContainer>
        <p>Loading devices...</p>
      </LoadingContainer>
    );
  }

  return (
    <DeviceContainer>
      <HeaderContainer>
        <DeviceTitle>Device Management</DeviceTitle>
        <button 
          onClick={() => setShowForm(true)} 
          className="btn btn-primary"
          disabled={showForm}
        >
          Add New Device
        </button>
      </HeaderContainer>

      {error && (
        <ErrorContainer>
          {error}
        </ErrorContainer>
      )}

      {!loading && (
        <>
          {showForm && (
            <div className="form-section" style={{ marginBottom: '2rem' }}>
              <h3>{editingDevice ? 'Edit Device' : 'Add New Device'}</h3>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label htmlFor="serialNumber">Serial Number *</label>
                    <input
                      type="text"
                      id="serialNumber"
                      className="form-control"
                      value={formData.serialNumber || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, serialNumber: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="sku">SKU *</label>
                    <input
                      type="text"
                      id="sku"
                      className="form-control"
                      value={formData.sku || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="customerName">Customer Name *</label>
                  <input
                    type="text"
                    id="customerName"
                    className="form-control"
                    value={formData.customerName || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label htmlFor="condition">Condition *</label>
                    <select
                      id="condition"
                      className="form-control"
                      value={formData.condition || 'good'}
                      onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value }))}
                      required
                    >
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                      <option value="damaged">Damaged</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="status">Status *</label>
                    <select
                      id="status"
                      className="form-control"
                      value={formData.status || 'received'}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      required
                    >
                      <option value="received">Received</option>
                      <option value="diagnosed">Diagnosed</option>
                      <option value="repaired">Repaired</option>
                      <option value="shipped">Shipped</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="issue">Issue Description</label>
                  <textarea
                    id="issue"
                    className="form-control"
                    rows={3}
                    value={formData.issue || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, issue: e.target.value }))}
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button type="submit" className="btn btn-primary">
                    {editingDevice ? 'Update Device' : 'Create Device'}
                  </button>
                  <button type="button" onClick={resetForm} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="form-section">
            <SectionTitle>Devices List ({devices.length} devices)</SectionTitle>
            <DeviceTable>
              <thead>
                <tr>
                  <TableHeader>Serial Number</TableHeader>
                  <TableHeader>SKU</TableHeader>
                  <TableHeader>Customer</TableHeader>
                  <TableHeader>Condition</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Issue</TableHeader>
                  <TableHeader>Received Date</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </tr>
              </thead>
              <tbody>
                {devices.map(device => (
                  <tr key={device.id}>
                    <TableCell><strong>{device.serialNumber}</strong></TableCell>
                    <TableCell>{device.sku}</TableCell>
                    <TableCell>{device.customerName}</TableCell>
                    <TableCell>{device.condition}</TableCell>
                    <TableCell>
                      <StatusBadge status={device.status}>
                        {device.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>{device.issue}</TableCell>
                    <TableCell>{new Date(device.receivedDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <ActionButton 
                        onClick={() => handleEdit(device)} 
                        $variant="secondary"
                      >
                        Edit
                      </ActionButton>
                      <ActionButton 
                        onClick={() => handleDeleteDevice(device)} 
                        $variant="danger"
                      >
                        Delete
                      </ActionButton>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </DeviceTable>

            {devices.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                No devices found. Click "Add New Device" to get started.
              </div>
            )}
          </div>
        </>
      )}
    </DeviceContainer>
  );
};

export default DeviceManagement;